import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Divider, Alert,
  Chip, Accordion, AccordionSummary, AccordionDetails, IconButton,
  Tooltip, Snackbar,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import CloudIcon from '@mui/icons-material/Cloud';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, googleAPI } from '../services/api';

const ROLE_LABELS = {
  admin: 'Administrador',
  manager: 'Gestor',
  engineer: 'Ingeniero',
  viewer: 'Solo lectura',
};

const MCP_URL = `${window.location.origin}/mcp/mcp`;

const AI_INSTRUCTIONS = `# Gestor de Proyectos Industriales (GPI)

Eres un asistente experto en gestión de proyectos industriales. Estás conectado a la aplicación GPI mediante herramientas MCP. Usa estas herramientas para consultar y modificar datos reales del sistema: lo que escribas queda guardado y lo verá todo el equipo.

## Qué es GPI

GPI gestiona proyectos de ingeniería industrial (plantas de detergentes, vinagres, lejías, cremas, mayonesa, líneas de proceso y de envasado) de principio a fin: desde la petición del cliente hasta la puesta en marcha en obra, pasando por ingeniería, compras, fabricación y planificación de los técnicos.

## Cómo debes trabajar

1. **Busca antes de crear.** search_clients, search_suppliers, search_technicians, list_projects. Un cliente o un proveedor duplicado arrastra después condiciones de pago y ofertas mal imputadas.
2. **Consulta el estado antes de cambiarlo.** get_project_full_summary da la foto completa de un proyecto en una sola llamada.
3. **No inventes datos.** Si falta información para crear algo, pregúntala. Es preferible una pregunta a un dato inventado que luego alguien da por bueno.
4. **Las listas de valores son configurables.** Los tipos de proyecto y de envase los define cada empresa: consúltalos con list_project_types y list_container_types. La API no valida esos campos, así que un valor inventado se guarda igual y deja el registro con un tipo que la interfaz no sabe etiquetar.
5. **Al actualizar, envía solo los campos que cambian.** Las herramientas update_* aceptan un diccionario parcial.
6. **Formatos**: fechas en AAAA-MM-DD e importes en euros, sin símbolo ni separador de millares.
7. **Los códigos se asignan solos**: no los inventes ni los sobrescribas.
8. **Avisa antes de acciones que mueven datos o son difíciles de deshacer**: avanzar de fase, aprobar una oferta o borrar registros.
9. Responde siempre en el mismo idioma que el usuario.

## Ciclo de vida de un proyecto

Las fases van en orden. advance_project_phase avanza un paso y rollback_project_phase retrocede:

1. **Oportunidad** (opportunity) — Llega la petición del cliente. Al crear el proyecto se le asigna su número de oferta (OF-AAAA-NNN) y se crea su carpeta en la rama OFERTAS de Google Drive.
2. **Ing. Conceptual** (conceptual) — Se define el proceso: materias primas, etapas, servicios auxiliares, formatos de envase y equipos.
3. **Preparación Oferta** (offer_preparation) — Ingeniería básica y partidas de coste.
4. **Oferta Enviada** (offer_sent) — La oferta está en manos del cliente.
5. **Oferta Aprobada** (offer_approved) — El cliente acepta.
6. **Ing. Detalle** (in_progress) — Planos, especificaciones y tareas de ingeniería. **Al entrar en esta fase se asigna el código de proyecto** (por ejemplo 26001).
7. **Ejecución** (commissioning) — Compras, fabricación, montaje y puesta en marcha. **Al entrar en esta fase la carpeta de la oferta se copia entera a la rama PROYECTOS de Drive** con el número de proyecto; la de ofertas se conserva.
8. **Completado** (completed) — Entregado.

Ojo con los dos números, que son distintos: offer_code (OF-2026-014) se asigna al crear la petición, y code (26001) al entrar en Ing. Detalle.

## Entidades y relaciones

### Clientes y proveedores
- **Clientes** (search/get/create/update/delete_client): name es obligatorio; country, continent (EUROPA, AFRICA, ASIA, AMERICA_NORTE, AMERICA_SUR, OCEANIA), contacto y actividad. El continente importa: es el primer nivel de la ruta de carpetas del proyecto en Drive.
- **Proveedores** (search/get/create/update/delete_supplier): mismos datos más categoría (equipos, tuberia, electrico, instrumentacion, civil, montaje, software).
- Ambos pueden tener **condiciones por defecto** (list/create/update/delete_client_default_condition y _supplier_default_condition): descripción, porcentaje e hito (pedido, entrega, factura, montaje, puesta_en_marcha). **No se copian solas**: usa copy_client_conditions_to_project o copy_supplier_conditions_to_project.

### Proyecto → Ingeniería
- **Conceptual** (get/update_conceptual_engineering): descripción del proceso, ritmo de producción, tamaño de lote, área estimada, consumos.
- **Básica** (get/update_basic_engineering): diagrama de flujo, P&ID, costes estimados y precio de venta.
- **Detalle** (get/update_detail_engineering): planos finales, programación PLC/SCADA, protocolos de FAT, SAT y puesta en marcha.

### Proyecto → Datos técnicos (todos usan project_id)
- **Materias primas** (list/add/update/delete_raw_material): cantidad por lote, proveedor, coste, almacenamiento y peligrosidad.
- **Etapas del proceso** (list/add/update/delete_process_stage): duración, temperatura, presión, entradas y salidas, parámetros críticos. Tipos: recepcion, dosificacion, mezclado, calentamiento, enfriamiento, homogeneizacion, filtracion, control, envasado, almacenamiento.
- **Servicios auxiliares** (list/add/update/delete_utility): electrical, water, steam, compressed_air, cooling, nitrogen, hot_water, wastewater.
- **Envasado** (list/add/update/delete_packaging_spec): volumen, material, cierre, etiqueta y velocidad. Tipos configurables (list_container_types).
- **Equipos** (list/add/update/delete_equipment): tag, capacidad, material, potencia, proveedor y coste.
- **Tareas de ingeniería** (list/add/update/delete_engineering_task): entregables de la ingeniería de detalle por disciplina. **No confundir con las tareas de campo.**
- **Planificación** (list/create/update/delete_planning_item, sync_planning): diagrama de barras del proyecto. sync_planning vuelca las tareas de ingeniería como líneas de planificación.

### Proyecto → Ofertas al cliente
- **Ofertas comerciales** (list/create/update/delete_project_offer): la oferta 1 se corresponde con la ingeniería básica. Las suplementarias (nº > 1) guardan sus equipos y partidas aparte (create_offer_equipment, create_offer_cost_entry) y **al aprobarlas con approve_project_offer esos datos se transfieren al proyecto**: no es un simple cambio de estado.
- La oferta no lleva importe propio: sale de sus equipos y partidas de coste.
- **Partidas de coste del proyecto** (list/add/update/delete_cost_entry): concepto, real_cost (coste real o estimado), offer_cost (lo que aparece en la oferta) y sale_price (precio de venta).

### Proyecto → Compras
- **Ofertas de proveedor** (list/add/update/delete_supplier_offer): presupuestos recibidos. Estados: pending, requested, received, validated y **final**. 'final' marca la oferta definitiva, que es la que se convierte en pedido. Cada una puede tener sus condiciones de pago (list/create/update/delete_supplier_offer_payment_condition, copy_supplier_defaults_to_offer).
- **Órdenes de fabricación** (list/create/update/delete_manufacturing_order, assign_offers_to_manufacturing_order, list_assignable_offers): agrupan ofertas de proveedor. Su código (26001-001) **es la referencia transversal del proyecto**: aparece en dibujos, pedidos, horas de calendario, tareas de obra y carpetas de fotos.
- **Pedidos de compra** (list/create/update/delete_purchase_order): nacen de una oferta de proveedor, de la que heredan proveedor y orden de fabricación. list_offers_without_order dice qué ofertas definitivas siguen sin pedir.
- **Condiciones de pago del pedido** (list/create/update/delete_order_payment_condition).

### Proyecto → Condiciones económicas
- **Cobros al cliente** (list/create/update/delete_project_client_condition, copy_client_conditions_to_project).
- **Pagos a proveedores** (list/create/update/delete_project_supplier_condition, copy_supplier_conditions_to_project, refresh_supplier_conditions para sincronizar desde las ofertas).

### Técnicos y calendario
- **Técnicos** (search/get/create/update/delete_technician, get_technician_summary): repertorio fijo con coste/hora, jornada y **fechas de alta y baja**. Solo aparecen en el calendario dentro de ese periodo.
- **Asignaciones** (list/create/update/delete_technician_assignment, get_technicians_calendar, get_technicians_workload): el recurso es un técnico fijo (technician_id) o un nombre libre de técnico o empresa puntual (resource_name). Se vinculan a un proyecto y a una orden de fabricación para imputar las horas.
- period_type day, week, month o range: con week o month el rango se ajusta solo a la semana o al mes de start_date. Las horas son días laborables por hours_per_day; vacation y absence no cuentan como horas productivas.
- **El calendario es la única fuente de horas planificadas.** Las reales salen de los partes de trabajo de las tareas.

### Tareas de campo (obra)
- **Tareas** (list/get/create/update/delete_work_task, assign_work_task, update_work_task_status, add_work_task_update): montaje, conexionado, pruebas y puesta en marcha. Estados: pending, assigned, in_progress, blocked, done, verified, cancelled. 'blocked' exige motivo y cerrar una tarea con requires_photo exige foto.
- **generate_tasks_from_manufacturing_order** vuelca el paquete estándar de una orden desde las plantillas (list_task_templates). Es idempotente: repetirlo solo añade lo que falta.
- Una tarea puede existir **sin orden de fabricación** (list_tasks_without_manufacturing_order, set_task_manufacturing_order, que reimputa sus horas).
- Lo que escribas en descripción, medidas de seguridad, herramientas y materiales **es lo que el técnico ve en el móvil**; las notas internas no se le muestran.
- **get_tasks_planned_vs_actual** compara horas previstas con las imputadas en los partes.
- **Fotos de obra** (list_pending_drive_photos, push_task_photos_to_drive): los técnicos no tienen cuenta de Google, así que sus fotos quedan en el servidor y un gestor las traslada a la carpeta del proyecto en Drive.

### Comunicaciones
- **Comunicaciones y pendientes** (list/add/update/delete_communication): entry_type 'communication' o 'pending'; tipos email, llamada, reunion, nota_interna, otro; los pendientes tienen prioridad, fecha límite y estado (open, in_progress, resolved). **list_overdue_pending** saca los pendientes vencidos de todos los proyectos: es la mejor forma de arrancar un repaso.

### Resumen completo
- **get_project_full_summary(project_id)**: datos generales, ingeniería, materias primas, etapas, equipos, envasado, servicios, ofertas de proveedor y comerciales, costes, pedidos, condiciones, comunicaciones y planificación en una sola llamada.

## Lo que no puedes hacer desde aquí

Explorar, subir o descargar documentos de Drive (salvo trasladar fotos de obra); presupuestos, hitos, facturas y flujo de caja; editar la configuración de la aplicación; crear usuarios o credenciales de acceso de los técnicos. Todo eso se hace desde la aplicación web.`;


function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Tooltip title={copied ? '¡Copiado!' : `Copiar ${label}`}>
      <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
        {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function CodeBlock({ code, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
      <Box
        component="pre"
        sx={{
          flex: 1,
          background: '#1e1e1e',
          color: '#d4d4d4',
          p: 1.5,
          borderRadius: 1,
          fontSize: '0.78rem',
          overflowX: 'auto',
          m: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {code}
      </Box>
      <CopyButton text={code} label={label || 'comando'} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Conexión de la cuenta de Google (Drive)
// ---------------------------------------------------------------------------
function GoogleDriveCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadStatus = async () => {
    try {
      const resp = await googleAPI.status();
      setStatus(resp.data);
    } catch (err) {
      setError('No se ha podido consultar el estado de la conexión');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Al volver de Google, la URL trae ?google=ok|error&msg=...
    const params = new URLSearchParams(window.location.search);
    const result = params.get('google');
    if (result === 'ok') {
      setNotice({ severity: 'success', text: `Cuenta conectada: ${params.get('email') || ''}` });
    } else if (result === 'error') {
      setNotice({ severity: 'error', text: params.get('msg') || 'No se pudo conectar la cuenta' });
    }
    if (result) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    loadStatus();
  }, []);

  const handleConnect = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await googleAPI.start();
      window.location.href = resp.data.auth_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido iniciar la conexión con Google');
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      await googleAPI.disconnect();
      await loadStatus();
      setNotice({ severity: 'info', text: 'Cuenta de Google desconectada' });
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido desconectar la cuenta');
    } finally {
      setBusy(false);
    }
  };

  const connected = status?.connected;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CloudIcon color="primary" />
        <Typography variant="h6">Cuenta de Google</Typography>
        {connected && <Chip label="Conectada" size="small" color="success"
          icon={<CheckCircleIcon />} variant="outlined" />}
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Conecta tu cuenta de Google Workspace para trabajar con los documentos de la unidad
        compartida desde la pestaña <strong>Documentos</strong>. La aplicación actúa en tu nombre:
        verás exactamente lo que tu cuenta puede ver y los cambios quedan registrados a tu nombre.
      </Typography>

      {notice && <Alert severity={notice.severity} sx={{ mb: 2 }}
        onClose={() => setNotice(null)}>{notice.text}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? <Typography variant="body2">Cargando…</Typography> : (
        <>
          {!status?.configured && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              La integración con Google no está configurada en el servidor
              (faltan GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET).
            </Alert>
          )}
          {status?.is_revoked && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              El acceso ha caducado o ha sido revocado. Vuelve a conectar la cuenta.
            </Alert>
          )}

          {connected ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField label="Cuenta conectada" value={status.google_email || ''}
                InputProps={{ readOnly: true }} size="small" sx={{ minWidth: 280 }} />
              <Button variant="outlined" color="error" onClick={handleDisconnect} disabled={busy}>
                Desconectar
              </Button>
            </Box>
          ) : (
            <Button variant="contained" onClick={handleConnect}
              disabled={busy || !status?.configured} startIcon={<CloudIcon />}>
              Conectar con Google
            </Button>
          )}

          {status?.documents_backend === 'drive' && !connected && (
            <Alert severity="info" sx={{ mt: 2 }}>
              El almacén documental está en Google Drive: sin conectar tu cuenta no podrás
              ver los documentos de los proyectos.
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const token = localStorage.getItem('token') || '';

  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwError, setPwError] = useState(null);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async () => {
    setPwError(null);
    if (!pwForm.current_password || !pwForm.new_password) {
      setPwError('Completa todos los campos');
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('Las contraseñas nuevas no coinciden');
      return;
    }
    if (pwForm.new_password.length < 6) {
      setPwError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      setSaving(true);
      await usersAPI.changePassword(pwForm.current_password, pwForm.new_password);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setPwSuccess(true);
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AccountCircleIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight="bold">Mi Cuenta</Typography>
      </Box>

      {/* Datos del usuario */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos de usuario</Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Nombre completo" value={user?.full_name || ''} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email" value={user?.email || ''} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Rol"
              value={ROLE_LABELS[user?.role] || user?.role || ''}
              InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Departamento"
              value={user?.department || '-'}
              InputProps={{ readOnly: true }} />
          </Grid>
        </Grid>
      </Paper>

      {/* Cambiar contraseña */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LockIcon color="action" />
          <Typography variant="h6">Cambiar contraseña</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {pwError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPwError(null)}>{pwError}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Contraseña actual" type="password"
              value={pwForm.current_password}
              onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Nueva contraseña" type="password"
              value={pwForm.new_password}
              onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Confirmar nueva contraseña" type="password"
              value={pwForm.confirm_password}
              onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleChangePassword} disabled={saving}>
            Cambiar contraseña
          </Button>
        </Box>
        <Snackbar
          open={pwSuccess}
          autoHideDuration={4000}
          onClose={() => setPwSuccess(false)}
          message="Contraseña cambiada correctamente"
        />
      </Paper>

      {/* Conexión con Google Drive */}
      <GoogleDriveCard />

      {/* Conexión con Claude AI */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SmartToyIcon color="primary" />
          <Typography variant="h6">Conectar con Claude AI</Typography>
          <Chip label="MCP" size="small" color="primary" variant="outlined" />
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Puedes conectar esta aplicación con Claude AI para gestionar proyectos, clientes y datos
          directamente desde el chat. La autorización se hace una sola vez desde el navegador —
          igual que con Gmail o Google Drive.
        </Typography>

        {/* Desde Claude.ai web */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Desde Claude.ai (navegador web)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Sigue estos pasos en <strong>claude.ai</strong>:
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 1.5 } }}>
              <li>
                <Typography variant="body2">
                  Ve a <strong>Claude.ai → Settings → Integrations</strong> y pulsa <em>"Add integration"</em>.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Introduce la URL del servidor MCP:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <TextField
                    size="small"
                    value={MCP_URL}
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                    sx={{ flex: 1 }}
                  />
                  <CopyButton text={MCP_URL} label="URL" />
                </Box>
              </li>
              <li>
                <Typography variant="body2">
                  Claude abrirá una ventana de autorización. Introduce tu email y contraseña de esta aplicación.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Listo. Claude renovará el acceso automáticamente sin que tengas que hacer nada más.
                </Typography>
              </li>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Desde ChatGPT */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Desde ChatGPT (chatgpt.com)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              ChatGPT no detecta automáticamente los endpoints OAuth. Debes introducirlos manualmente
              en <strong>chatgpt.com → Tu nombre → Configuración → Conectores → Nueva aplicación</strong>:
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 2 } }}>
              <li>
                <Typography variant="body2" gutterBottom>
                  <strong>URL del servidor MCP</strong>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField size="small" value={MCP_URL}
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                    sx={{ flex: 1 }} />
                  <CopyButton text={MCP_URL} label="URL MCP" />
                </Box>
              </li>
              <li>
                <Typography variant="body2" gutterBottom>
                  Selecciona <strong>Autenticación: OAuth</strong> y despliega <em>"Configuración avanzada de OAuth"</em>.
                  Rellena estos campos:
                </Typography>
                {[
                  { label: 'URL de autenticación',           value: `${MCP_URL}/oauth/authorize` },
                  { label: 'Token URL',                      value: `${MCP_URL}/oauth/token` },
                  { label: 'URL de registro',                value: `${MCP_URL}/oauth/register` },
                  { label: 'Base del servidor de autorización', value: MCP_URL },
                  { label: 'Recurso',                        value: MCP_URL },
                ].map(({ label, value }) => (
                  <Box key={label} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField size="small" value={value}
                        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
                        sx={{ flex: 1 }} />
                      <CopyButton text={value} label={label} />
                    </Box>
                  </Box>
                ))}
              </li>
              <li>
                <Typography variant="body2">
                  Pulsa <strong>Crear</strong>. ChatGPT abrirá la página de login de GPI — introduce tu
                  email y contraseña de esta aplicación para autorizar el acceso.
                </Typography>
              </li>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Desde Claude Code */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Desde Claude Code (terminal)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Ejecuta este comando en tu terminal. Claude abrirá el navegador automáticamente para
              que te autentiques:
            </Typography>
            <CodeBlock
              code={`claude mcp add gpi --transport http ${MCP_URL}`}
              label="comando de registro"
            />
            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              Verifica que está registrado:
            </Typography>
            <CodeBlock code="claude mcp list" label="comando de verificación" />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              A partir de ahí puedes pedir cosas como: <em>"Dame un resumen del proyecto 12"</em>,{' '}
              <em>"Crea un proyecto nuevo para el cliente Acme"</em>, etc.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Instrucciones para la AI */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">¿Cómo utilizar la AI?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Para que Claude trabaje de forma inteligente con tus proyectos, necesita unas instrucciones
              que le expliquen cómo funciona la aplicación. Estas instrucciones se configuran <strong>una sola vez</strong> en
              cada proyecto de Claude.ai:
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 1.5 } }}>
              <li>
                <Typography variant="body2">
                  En <strong>claude.ai</strong>, crea un <strong>Proyecto</strong> (o abre uno existente).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Copia el texto de abajo pulsando el botón <strong>Copiar</strong>.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Pégalo en las <strong>instrucciones del proyecto</strong> (Project instructions).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Listo. A partir de ahora Claude entenderá la estructura de GPI y podrá gestionar
                  clientes, proyectos, ingeniería, ofertas, pedidos y planificación directamente.
                </Typography>
              </li>
            </Box>

            <Box sx={{
              mt: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                bgcolor: 'action.hover',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}>
                <Typography variant="subtitle2">Instrucciones para Claude</Typography>
                <CopyButton text={AI_INSTRUCTIONS} label="instrucciones" />
              </Box>
              <Box
                component="pre"
                sx={{
                  p: 2,
                  m: 0,
                  maxHeight: 400,
                  overflowY: 'auto',
                  fontSize: '0.75rem',
                  lineHeight: 1.5,
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  bgcolor: '#1e1e1e',
                  color: '#d4d4d4',
                }}
              >
                {AI_INSTRUCTIONS}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Token manual (avanzado) */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Uso avanzado — token de sesión actual</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Si necesitas usar el token directamente (p.ej. para scripts o pruebas), aquí está
              tu token de sesión activo. <strong>Caduca al cerrar sesión o tras 8 horas.</strong>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={token}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.72rem' },
                }}
                multiline
                maxRows={3}
              />
              <CopyButton text={token} label="token" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.78rem' }}>
              Ejemplo de uso con curl:
            </Typography>
            <CodeBlock
              code={`curl -s ${window.location.origin}/api/clients/ \\\n  -H "Authorization: Bearer ${token.slice(0, 20)}..."`}
              label="ejemplo curl"
            />
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Box>
  );
}
